import { Zap, CheckCircle, Clock, MapPin, ArrowRight, Battery } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "../ui/button";
import { Card, CardContent } from "../ui/card";
import { SEOHead } from "../SEOHead";
import { Header } from "../Header";
import { Footer } from "../Footer";
import { FloatingContactButtons } from "../FloatingContactButtons";
import { companyInfo } from "../../data/mock";

export const JumpStartPage = () => {
  return (
    <div className="min-h-screen bg-slate-50">
      <SEOHead 
        title="Jump Start Service Hackney | Battery Boost East London | 24/7 Jump Start"
        description="Fast jump start service in Hackney & East London. 30-min response for dead battery. 24/7 emergency jump start & battery boost. Call 07492 639223 for immediate help."
        keywords="jump start service hackney, battery boost east london, dead battery help, car won't start hackney, battery jump start london, mobile jump start service, emergency battery boost"
      />
      <Header />

      {/* Hero Section */}
      <section className="bg-gradient-to-br from-yellow-600 via-yellow-500 to-orange-600 text-white py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-block bg-yellow-700 px-4 py-2 rounded-full text-sm font-semibold mb-4">
              ⚡ Fast 30-Min Response
            </div>
            <h1 className="text-4xl md:text-6xl font-bold mb-6">
              Jump Start Service in Hackney & East London
            </h1>
            <p className="text-xl text-yellow-50 mb-8">
              Dead battery? Don't panic. Our mobile jump start service reaches you in 30 minutes across Hackney, Shoreditch, Stratford and all East London areas. Available 24/7 for immediate battery boost assistance.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a href={`tel:${companyInfo.phone}`}>
                <Button size="lg" className="bg-white text-yellow-900 hover:bg-slate-100 text-lg px-8 py-6">
                  <Zap className="w-5 h-5 mr-2" />
                  Call Now: {companyInfo.phone}
                </Button>
              </a>
              <Link to="/#quote">
                <Button size="lg" variant="outline" className="border-white text-white hover:bg-white hover:text-yellow-900 text-lg px-8 py-6">
                  Get Quote
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Trust Indicators */}
      <section className="py-8 bg-white border-b">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 max-w-4xl mx-auto">
            <div className="text-center">
              <p className="text-3xl font-bold text-yellow-600">30 Min</p>
              <p className="text-sm text-slate-600">Average Arrival</p>
            </div>
            <div className="text-center">
              <p className="text-3xl font-bold text-yellow-600">24/7</p>
              <p className="text-sm text-slate-600">Available Anytime</p>
            </div>
            <div className="text-center">
              <p className="text-3xl font-bold text-yellow-600">£45+</p>
              <p className="text-sm text-slate-600">Fixed Pricing</p>
            </div>
            <div className="text-center">
              <p className="text-3xl font-bold text-yellow-600">Local</p>
              <p className="text-sm text-slate-600">Hackney Based</p>
            </div>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-slate-900 mb-6">
              Professional Jump Start Service in Hackney
            </h2>
            <p className="text-lg text-slate-700 leading-relaxed mb-6">
              A dead car battery is one of the most common roadside emergencies. Whether you left your lights on, your battery is old, or it's just freezing cold weather, Ovawell Autos provides fast, professional jump start services across Hackney and East London. Our mobile technicians carry professional-grade jump start equipment and can get your car running in minutes.
            </p>

            <h3 className="text-2xl font-bold text-slate-900 mb-4 mt-8">
              Our Jump Start Service Includes
            </h3>
            <div className="grid md:grid-cols-2 gap-4 mb-8">
              {[
                "Professional battery testing",
                "Safe jump start procedure",
                "Battery health check",
                "Charging system inspection",
                "Battery replacement advice",
                "Terminal cleaning (if needed)",
                "Alternator check",
                "Free battery tips"
              ].map((item, index) => (
                <div key={index} className="flex items-start gap-3">
                  <CheckCircle className="w-6 h-6 text-yellow-600 flex-shrink-0 mt-1" />
                  <span className="text-slate-700">{item}</span>
                </div>
              ))}
            </div>

            <h3 className="text-2xl font-bold text-slate-900 mb-4 mt-8">
              Why Choose Ovawell Autos for Jump Start Services?
            </h3>
            <div className="space-y-4 mb-8">
              <Card>
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <Clock className="w-8 h-8 text-yellow-600 flex-shrink-0" />
                    <div>
                      <h4 className="font-bold text-lg text-slate-900 mb-2">30-Minute Response Time</h4>
                      <p className="text-slate-600">
                        Based in Hackney, we reach most East London locations within 30 minutes. Our local knowledge means faster service than national recovery companies.
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <Battery className="w-8 h-8 text-yellow-600 flex-shrink-0" />
                    <div>
                      <h4 className="font-bold text-lg text-slate-900 mb-2">Professional Equipment</h4>
                      <p className="text-slate-600">
                        We use professional-grade jump starters and battery testers. We don't just boost your battery - we diagnose why it failed and advise on next steps.
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <MapPin className="w-8 h-8 text-yellow-600 flex-shrink-0" />
                    <div>
                      <h4 className="font-bold text-lg text-slate-900 mb-2">Anywhere in Hackney & East London</h4>
                      <p className="text-slate-600">
                        Home, work, supermarket car park, or roadside - we come to you. Covering all Hackney postcodes (E5, E8, E9) plus Shoreditch, Stratford, Islington, and surrounding areas.
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            <h3 className="text-2xl font-bold text-slate-900 mb-4 mt-8">
              Common Reasons for Dead Battery
            </h3>
            <ul className="list-disc list-inside space-y-2 text-slate-700 mb-8">
              <li><strong>Lights left on:</strong> Interior or headlights left on overnight</li>
              <li><strong>Cold weather:</strong> Freezing temperatures reduce battery capacity by 30-50%</li>
              <li><strong>Old battery:</strong> Most car batteries last 3-5 years</li>
              <li><strong>Parasitic drain:</strong> Faulty electronics draining power when car is off</li>
              <li><strong>Short journeys:</strong> Battery doesn't get enough time to recharge</li>
              <li><strong>Faulty alternator:</strong> Charging system not replenishing battery</li>
              <li><strong>Corroded terminals:</strong> Poor connection prevents charging</li>
            </ul>

            <h3 className="text-2xl font-bold text-slate-900 mb-4 mt-8">
              Areas We Cover for Jump Start Services
            </h3>
            <div className="grid md:grid-cols-3 gap-4 mb-8">
              {[
                "Hackney Central",
                "Hackney Wick",
                "Dalston",
                "Shoreditch",
                "Hoxton",
                "Homerton",
                "Stratford",
                "Bow",
                "Mile End",
                "Bethnal Green",
                "London Fields",
                "Clapton",
                "Stoke Newington",
                "Victoria Park",
                "Islington",
                "Tower Hamlets"
              ].map((area, index) => (
                <div key={index} className="flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-yellow-600 flex-shrink-0" />
                  <span className="text-slate-700">{area}</span>
                </div>
              ))}
            </div>

            <h3 className="text-2xl font-bold text-slate-900 mb-4 mt-8">
              How Our Jump Start Service Works
            </h3>
            <div className="grid md:grid-cols-4 gap-6 mb-8">
              <Card>
                <CardContent className="p-6 text-center">
                  <div className="w-12 h-12 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <span className="text-2xl font-bold text-yellow-600">1</span>
                  </div>
                  <h4 className="font-bold mb-2">Call Us</h4>
                  <p className="text-sm text-slate-600">Contact us immediately - available 24/7</p>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-6 text-center">
                  <div className="w-12 h-12 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <span className="text-2xl font-bold text-yellow-600">2</span>
                  </div>
                  <h4 className="font-bold mb-2">We Arrive Fast</h4>
                  <p className="text-sm text-slate-600">30-min average response across Hackney</p>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-6 text-center">
                  <div className="w-12 h-12 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <span className="text-2xl font-bold text-yellow-600">3</span>
                  </div>
                  <h4 className="font-bold mb-2">Jump Start</h4>
                  <p className="text-sm text-slate-600">Professional equipment gets you running</p>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-6 text-center">
                  <div className="w-12 h-12 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <span className="text-2xl font-bold text-yellow-600">4</span>
                  </div>
                  <h4 className="font-bold mb-2">Battery Check</h4>
                  <p className="text-sm text-slate-600">We test and advise on battery health</p>
                </CardContent>
              </Card>
            </div>

            <h3 className="text-2xl font-bold text-slate-900 mb-4 mt-8">
              Jump Start Service Pricing
            </h3>
            <Card className="mb-8">
              <CardContent className="p-6">
                <div className="flex justify-between items-center">
                  <div>
                    <h4 className="font-bold text-lg mb-2">Jump Start Service</h4>
                    <p className="text-slate-600">Includes arrival, jump start, battery test, and professional advice</p>
                    <p className="text-sm text-slate-500 mt-2">Fixed price • No hidden fees • 24/7 same rate</p>
                  </div>
                  <div className="text-right">
                    <p className="text-3xl font-bold text-yellow-600">£45+</p>
                    <p className="text-sm text-slate-500">From</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <h3 className="text-2xl font-bold text-slate-900 mb-4 mt-8">
              Frequently Asked Questions
            </h3>
            <div className="space-y-4 mb-8">
              <Card>
                <CardContent className="p-6">
                  <h4 className="font-bold text-lg mb-2">How long does a jump start take?</h4>
                  <p className="text-slate-600">
                    The actual jump start takes 5-10 minutes. We'll also run a battery health check and charging system test to ensure you don't break down again immediately.
                  </p>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-6">
                  <h4 className="font-bold text-lg mb-2">Will my car start again after being jumped?</h4>
                  <p className="text-slate-600">
                    Most cars will run fine if the battery just needed a boost. However, if your battery is old or damaged, you may need a replacement. We'll advise you after testing.
                  </p>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-6">
                  <h4 className="font-bold text-lg mb-2">Do you work in cold weather?</h4>
                  <p className="text-slate-600">
                    Yes! Cold weather is the most common cause of dead batteries. We operate 24/7/365 regardless of weather conditions.
                  </p>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-6">
                  <h4 className="font-bold text-lg mb-2">Can you replace my battery if needed?</h4>
                  <p className="text-slate-600">
                    We can jump start your car and advise on battery replacement. We recommend taking your car to a local garage for battery replacement to ensure correct installation and disposal.
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-gradient-to-br from-yellow-600 to-orange-600 text-white py-16">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            Car Won't Start? We're On Our Way
          </h2>
          <p className="text-xl text-yellow-50 mb-8 max-w-2xl mx-auto">
            Don't wait in the cold. Call now for immediate jump start service in Hackney & East London.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href={`tel:${companyInfo.phone}`}>
              <Button size="lg" className="bg-white text-yellow-900 hover:bg-slate-100 text-lg px-8 py-6">
                <Zap className="w-5 h-5 mr-2" />
                Call {companyInfo.phone}
              </Button>
            </a>
            <Link to="/#quote">
              <Button size="lg" variant="outline" className="border-white text-white hover:bg-white hover:text-yellow-900 text-lg px-8 py-6">
                Get Quick Quote
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <Footer />
      <FloatingContactButtons />
    </div>
  );
};
